// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 3, Iter 331).
// Verkapselt die Buchungs- und Edit-Logik:
//   - handleBookingSubmit (Neu-Buchung inkl. Serien, Gap-Warnung,
//     Customer-Konflikt-Preflight, Slot-Validierung)
//   - confirmBookingWithConflicts (Serie trotz Konflikt buchen)
//   - handleEditSubmit (Termin bearbeiten)
//   - checkCustomerConflicts (Hinweise für selben Tag / Vortag / Folgetag)
//   - checkSeriesConflicts (vorab Konflikte für jede Serien-Wiederholung)
//
// Die Hook-Signatur ist breit, weil die Buchungs-Logik viel UI- und
// Daten-State benötigt. Der Verhaltens-Kontrakt ist identisch zum
// vorherigen Inline-Code.

import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { runCustomerConflictCheck } from '@/utils/customerConflictCheck';
import { addSeriesJob } from '@/lib/seriesJobsStore';
import { swissWallTimeToISO, toLocalYMD, getSwissYMD, getSwissHM } from '@/components/appointments/calendarUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useBookingActions = ({
  // User-Context
  user,
  // Booking-Form State
  bookingData,
  customTime,
  setCustomTime,
  selectedSlot,
  selectedTrainer,
  selectedAppointment,
  setSelectedAppointment,
  // Recurring
  isRecurring,
  setIsRecurring,
  recurringFrequency,
  setRecurringFrequency,
  recurringOccurrences,
  setRecurringOccurrences,
  // Submission state
  isBooking,
  setIsBooking,
  // Dialogs
  setBookingDialogOpen,
  setIsAddingParticipant,
  setEditDialogOpen,
  setOverwriteWarningDialogOpen,
  pendingBookingData,
  setPendingBookingData,
  setConflictingDates,
  setSeriesSkippedInfo,
  // Calendar-Data
  appointments,
  customers,
  services,
  currentDate,
  studioFilter,
  fetchAppointments,
  notifyCustomer = true,
}) => {
  // Eingabefeld darf temporär leer sein — für Logik immer gültige Zahl 1-52
  const occurrencesNum = Math.min(300, Math.max(1, parseInt(recurringOccurrences, 10) || 1));

  // 08/2026: Serien-Sync-Fortschritt (Mindbody-Push nach grosser Serie)
  const [seriesSyncId, setSeriesSyncId] = useState(null);

  // ──────────────────────────────────────────────────────────────────
  //  Helpers
  // ──────────────────────────────────────────────────────────────────
  const checkCustomerConflicts = useCallback(
    (appointmentData) => {
      const warnings = [];
      const customer = customers.find((c) => c.id === appointmentData.customer_id);
      const customerName = customer
        ? `${customer.first_name} ${customer.last_name}`
        : 'Dieser Kunde';

      const newStart = new Date(appointmentData.start_time);
      const newDate = newStart.toDateString();

      // Check for same service on same day
      const sameDayAppointments = appointments.filter((apt) => {
        const aptStart = new Date(apt.start_time);
        const customerIds = apt.customer_ids || [apt.customer_id];
        return (
          aptStart.toDateString() === newDate &&
          customerIds.includes(appointmentData.customer_id) &&
          apt.service_id === appointmentData.service_id
        );
      });
      if (sameDayAppointments.length > 0) {
        warnings.push({
          type: 'same_day_same_service',
          message: `${customerName} hat bereits einen Termin für diesen Service heute!`,
        });
      }

      const yesterday = new Date(newStart);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toDateString();
      const dayBeforeApt = appointments.find((apt) => {
        const aptStart = new Date(apt.start_time);
        const customerIds = apt.customer_ids || [apt.customer_id];
        return (
          aptStart.toDateString() === yesterdayStr &&
          customerIds.includes(appointmentData.customer_id)
        );
      });
      if (dayBeforeApt) {
        warnings.push({
          type: 'day_before',
          message: `ℹ️ ${customerName} hatte gestern einen Termin (${dayBeforeApt.service_name}).`,
        });
      }

      const tomorrow = new Date(newStart);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toDateString();
      const dayAfterApt = appointments.find((apt) => {
        const aptStart = new Date(apt.start_time);
        const customerIds = apt.customer_ids || [apt.customer_id];
        return (
          aptStart.toDateString() === tomorrowStr &&
          customerIds.includes(appointmentData.customer_id)
        );
      });
      if (dayAfterApt) {
        warnings.push({
          type: 'day_after',
          message: `ℹ️ ${customerName} hat morgen einen Termin (${dayAfterApt.service_name}).`,
        });
      }

      return warnings;
    },
    [appointments, customers]
  );

  const checkSeriesConflicts = useCallback(
    async (appointmentData) => {
      const conflicts = [];
      const startDate = new Date(appointmentData.start_time);
      const endDate = new Date(appointmentData.end_time);
      const duration = endDate - startDate;

      const service = services.find((s) => s.id === appointmentData.service_id);
      const maxParticipants = service?.max_participants || 1;

      for (let i = 0; i < occurrencesNum; i++) {
        const occurrenceStart = new Date(startDate);
        const daysIncrement =
          recurringFrequency === 'daily' ? 1
            : recurringFrequency === 'biweekly' ? 14
              : recurringFrequency === 'monthly' ? 30
                : 7;
        occurrenceStart.setDate(occurrenceStart.getDate() + i * daysIncrement);
        const occurrenceEnd = new Date(occurrenceStart.getTime() + duration);

        const existingApt = appointments.find((apt) => {
          if (apt.trainer_id !== appointmentData.trainer_id) return false;
          const aptStart = new Date(apt.start_time);
          const aptEnd = new Date(apt.end_time);
          return aptStart < occurrenceEnd && aptEnd > occurrenceStart;
        });

        if (existingApt) {
          if (existingApt.service_id === appointmentData.service_id) {
            const currentParticipants = existingApt.customer_ids?.length || 1;
            const newParticipants = appointmentData.customer_ids?.length || 1;
            if (currentParticipants + newParticipants > maxParticipants) {
              conflicts.push({
                date: occurrenceStart.toLocaleDateString('de-DE'),
                time: `${occurrenceStart.getHours().toString().padStart(2, '0')}:${occurrenceStart.getMinutes().toString().padStart(2, '0')}`,
                existingService: existingApt.service_name,
                existingCustomer:
                  existingApt.customer_names?.[0] ||
                  existingApt.customer_name ||
                  'Unbekannt',
              });
            }
          } else {
            conflicts.push({
              date: occurrenceStart.toLocaleDateString('de-DE'),
              time: `${occurrenceStart.getHours().toString().padStart(2, '0')}:${occurrenceStart.getMinutes().toString().padStart(2, '0')}`,
              existingService: existingApt.service_name,
              existingCustomer:
                existingApt.customer_names?.[0] ||
                existingApt.customer_name ||
                'Unbekannt',
            });
          }
        }
      }
      return conflicts;
    },
    [appointments, services, recurringFrequency, recurringOccurrences]
  );

  // ──────────────────────────────────────────────────────────────────
  //  Booking
  // ──────────────────────────────────────────────────────────────────
  const handleBookingSubmit = async () => {
    // Verhindere Doppelklick
    if (isBooking) return;

    if (!bookingData.service_id || !bookingData.customer_id) {
      toast.error('Bitte Service und Kunde auswählen');
      return;
    }
    if (customTime && !/^\d{2}:\d{2}$/.test(customTime)) {
      toast.error('Ungültiges Zeitformat. Bitte HH:MM verwenden (z.B. 06:15)');
      return;
    }

    setIsBooking(true);

    // Auto-correct to nearest 15-minute increment
    let correctedTime = customTime;
    if (customTime) {
      const [hours, minutes] = customTime.split(':').map(Number);
      const validMinutes = [0, 15, 30, 45];
      let nearestMinute = validMinutes[0];
      let smallestDiff = Math.abs(minutes - validMinutes[0]);
      for (const validMin of validMinutes) {
        const diff = Math.abs(minutes - validMin);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          nearestMinute = validMin;
        }
      }
      if (minutes > 45 && nearestMinute === 0) {
        const newHours = hours + 1;
        correctedTime = `${newHours.toString().padStart(2, '0')}:00`;
      } else {
        correctedTime = `${hours.toString().padStart(2, '0')}:${nearestMinute.toString().padStart(2, '0')}`;
      }
      if (correctedTime !== customTime) {
        toast.info(`Zeit automatisch angepasst: ${customTime} → ${correctedTime}`);
        setCustomTime(correctedTime);
      }
    }

    try {
      const service = services.find((s) => s.id === bookingData.service_id);
      if (!service) {
        toast.error('Service nicht gefunden');
        return;
      }

      const timeToUse = correctedTime || selectedSlot;
      const [hours, minutes] = timeToUse.split(':').map(Number);
      // 08/2026 TZ-Fix: Startzeit als Schweizer Wanduhr-Zeit ankern
      // (nicht browser-lokal), sonst verschieben sich Termine auf
      // Nicht-CH-Browsern (z.B. 06:30 → 08:30).
      const startIso = swissWallTimeToISO(toLocalYMD(currentDate), hours, minutes);
      const startTime = new Date(startIso);
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + (parseInt(bookingData.duration_override) || service.duration_minutes));

      const currentUserRole = user?.role || 'admin';
      const currentUserName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
      const bookingSource =
        currentUserRole === 'trainer'
          ? `trainer:${currentUserName}`
          : currentUserRole === 'admin'
            ? `admin:${currentUserName}`
            : 'web';

      const appointmentData = {
        service_id: bookingData.service_id,
        customer_id: bookingData.customer_id,
        trainer_id: selectedTrainer.trainer.id,
        resource_ids: [],
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        notes: bookingData.notes,
        booking_source: bookingSource,
        payment_method: 'card',
        selected_variant: bookingData.selected_variant || null,
        ...(studioFilter && studioFilter !== 'all'
          ? { studio_id: studioFilter }
          : {}),
        is_series: isRecurring,
        notify_customer: notifyCustomer,
        series_config: isRecurring
          ? {
              frequency: recurringFrequency,
              occurrences: occurrencesNum,
            }
          : null,
      };

      // Duplicate-Check (gleicher Kunde im selben Termin)
      const existingApt = appointments.find((apt) => {
        if (apt.trainer_id !== appointmentData.trainer_id) return false;
        const aptStart = new Date(apt.start_time);
        const aptEnd = new Date(apt.end_time);
        const newStart = new Date(appointmentData.start_time);
        const newEnd = new Date(appointmentData.end_time);
        return aptStart < newEnd && aptEnd > newStart;
      });
      if (existingApt) {
        const customerIds = existingApt.customer_ids || [existingApt.customer_id];
        if (customerIds.includes(bookingData.customer_id)) {
          toast.error('Dieser Kunde ist bereits zu diesem Termin angemeldet!');
          return;
        }
      }

      // Backend-Preflight
      const proceedAfterConflictCheck = await runCustomerConflictCheck(
        bookingData.customer_id,
        appointmentData.start_time,
        null,
        customers
      );
      if (!proceedAfterConflictCheck) return;

      // Legacy client-side check
      const customerWarnings = checkCustomerConflicts(appointmentData);
      if (customerWarnings.length > 0) {
        const warningMessages = customerWarnings.map((w) => w.message).join('\n');
        if (!window.confirm(`⚠️ HINWEISE:\n\n${warningMessages}\n\nTrotzdem buchen?`)) {
          return;
        }
      }

      // Series conflicts
      if (isRecurring) {
        const conflicts = await checkSeriesConflicts(appointmentData);
        if (conflicts.length > 0) {
          setConflictingDates(conflicts);
          setPendingBookingData(appointmentData);
          setOverwriteWarningDialogOpen(true);
          setIsBooking(false);
          return;
        }
      }

      // 06/2026 Gap-Warning
      const _newStartMs = new Date(appointmentData.start_time).getTime();
      const _newEndMs = new Date(appointmentData.end_time).getTime();
      const _serviceDuration = service.duration_minutes || 30;
      const _isShortService = _serviceDuration <= 15;
      if (!_isShortService) {
        const _sameTrainerSameDay = appointments.filter((a) => {
          if (a.trainer_id !== appointmentData.trainer_id) return false;
          if (a.status === 'cancelled' || a.status === 'no_show') return false;
          const aDate = new Date(a.start_time).toDateString();
          return aDate === new Date(appointmentData.start_time).toDateString();
        });
        const _gapBefore = _sameTrainerSameDay.find((a) => {
          const aEnd = new Date(a.end_time).getTime();
          const diff = _newStartMs - aEnd;
          return diff > 0 && diff < 30 * 60 * 1000;
        });
        const _gapAfter = _sameTrainerSameDay.find((a) => {
          const aStart = new Date(a.start_time).getTime();
          const diff = aStart - _newEndMs;
          return diff > 0 && diff < 30 * 60 * 1000;
        });
        if (_gapBefore || _gapAfter) {
          const _formatT = (iso) => {
            const d = new Date(iso);
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
          };
          const _msgs = [];
          if (_gapBefore) {
            _msgs.push(
              `• ${Math.round((_newStartMs - new Date(_gapBefore.end_time).getTime()) / 60000)}-Min-Lücke zwischen ${_formatT(_gapBefore.end_time)} und ${_formatT(appointmentData.start_time)}`
            );
          }
          if (_gapAfter) {
            _msgs.push(
              `• ${Math.round((new Date(_gapAfter.start_time).getTime() - _newEndMs) / 60000)}-Min-Lücke zwischen ${_formatT(appointmentData.end_time)} und ${_formatT(_gapAfter.start_time)}`
            );
          }
          const _ok = window.confirm(
            `⚠️ Du erzeugst eine ungenutzte Lücke im Kalender:\n\n${_msgs.join('\n')}\n\n` +
              `In so kurze Lücken passt nur Kryosauna (15 Min).\nTrotzdem buchen?`
          );
          if (!_ok) {
            setIsBooking(false);
            return;
          }
        }
      }

      const response = await axios.post(`${API}/appointments`, appointmentData, { timeout: 120000 });

      const seriesInfo = response.data?._series_info;
      if (isRecurring && response.data?.series_id) {
        addSeriesJob(response.data.series_id);
      }
      if (isRecurring && seriesInfo && seriesInfo.skipped_count > 0) {
        setSeriesSkippedInfo(seriesInfo);
      } else if (isRecurring) {
        toast.success('Termine werden im Hintergrund gebucht — du kannst weiterarbeiten.');
      } else {
        toast.success('Termin erfolgreich gebucht!');
      }

      setBookingDialogOpen(false);
      setCustomTime('');
      setIsRecurring(false);
      setRecurringFrequency('weekly');
      setRecurringOccurrences(10);
      fetchAppointments();
    } catch (error) {
      // 08/2026: Timeout/Abbruch bei grossen Serien → klare Rückmeldung statt Stille
      if (error.code === 'ECONNABORTED' || !error.response) {
        toast.warning('Die Serie dauert länger als erwartet — die Termine werden im Hintergrund fertig erstellt. Kalender wird gleich aktualisiert…', { duration: 8000 });
        setBookingDialogOpen(false);
        setTimeout(() => fetchAppointments(), 5000);
      } else {
        const d = error.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Fehler beim Buchen');
      }
    } finally {
      setIsBooking(false);
    }
  };

  const confirmBookingWithConflicts = async () => {
    try {
      const response = await axios.post(`${API}/appointments`, pendingBookingData, { timeout: 120000 });

      const seriesInfo = response.data?._series_info;
      if (response.data?.series_id) {
        addSeriesJob(response.data.series_id);
      }
      if (seriesInfo && seriesInfo.skipped_count > 0) {
        setSeriesSkippedInfo(seriesInfo);
      } else {
        toast.success('Termine werden im Hintergrund gebucht — du kannst weiterarbeiten.');
      }

      setOverwriteWarningDialogOpen(false);
      setBookingDialogOpen(false);
      setIsAddingParticipant(false);
      setConflictingDates([]);
      setPendingBookingData(null);
      setCustomTime('');
      setIsRecurring(false);
      setRecurringFrequency('weekly');
      setRecurringOccurrences(10);
      fetchAppointments();
    } catch (error) {
      if (error.code === 'ECONNABORTED' || !error.response) {
        toast.warning('Die Serie dauert länger als erwartet — die Termine werden im Hintergrund fertig erstellt. Kalender wird gleich aktualisiert…', { duration: 8000 });
        setTimeout(() => fetchAppointments(), 5000);
      } else {
        const d = error.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Fehler beim Buchen');
      }
    }
  };

  const handleEditSubmit = async () => {
    if (!bookingData.service_id || !bookingData.customer_id) {
      toast.error('Bitte Service und Kunde auswählen');
      return;
    }
    if (customTime && !/^\d{2}:\d{2}$/.test(customTime)) {
      toast.error('Ungültiges Zeitformat. Bitte HH:MM verwenden (z.B. 06:15)');
      return;
    }

    try {
      const service = services.find((s) => s.id === bookingData.service_id);
      const [hours, minutes] = customTime.split(':').map(Number);
      // 08/2026 TZ-Fix: Wanduhr-Zeit auf dem Schweizer Kalendertag des
      // Original-Termins ankern (nicht browser-lokal).
      const startIso = swissWallTimeToISO(
        getSwissYMD(selectedAppointment.start_time), hours, minutes
      );
      const startTime = new Date(startIso);
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + (parseInt(bookingData.duration_override) || service.duration_minutes));

      const updateData = {
        service_id: bookingData.service_id,
        customer_id: bookingData.customer_id,
        notes: bookingData.notes,
      };
      // Zeit/Dauer NUR mitsenden, wenn der Nutzer die Felder TATSÄCHLICH
      // geändert hat (Vergleich mit den Vorbefüllungswerten — exakt wie beim
      // Prefill abgeleitet). Sonst würde a) die abweichende ISO-Formatierung
      // und b) eine aus der Service-Dauer „korrigierte" end_time die
      // Abrechnungs-Sperre (409) bei bereits abgerechneten Terminen auslösen —
      // obwohl z.B. nur das Studio korrigiert werden soll.
      const { hour: _ph, minute: _pm } = getSwissHM(selectedAppointment.start_time);
      const prefilledTime = `${String(_ph).padStart(2, '0')}:${String(_pm).padStart(2, '0')}`;
      let _prefDur = Number(selectedAppointment.duration) || null;
      if (!_prefDur && selectedAppointment.end_time) {
        const _d = Math.round(
          (new Date(selectedAppointment.end_time) - new Date(selectedAppointment.start_time)) / 60000
        );
        if (_d > 0) _prefDur = _d;
      }
      const timeUntouched = customTime === prefilledTime;
      const durationUntouched =
        String(bookingData.duration_override ?? '') === String(_prefDur || '');
      if (!timeUntouched || !durationUntouched) {
        updateData.start_time = startTime.toISOString();
        updateData.end_time = endTime.toISOString();
      }
      // Studio-Korrektur (10/2026): nur mitsenden, wenn im Dialog geändert.
      if (bookingData.studio_id) {
        updateData.studio_id = bookingData.studio_id;
      }

      const proceedEdit = await runCustomerConflictCheck(
        bookingData.customer_id,
        startTime.toISOString(),
        selectedAppointment.id,
        customers
      );
      if (!proceedEdit) return;

      await axios.put(`${API}/appointments/${selectedAppointment.id}`, updateData);
      toast.success('Termin aktualisiert!');
      setEditDialogOpen(false);
      setSelectedAppointment(null);
      setCustomTime('');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  return {
    handleBookingSubmit,
    confirmBookingWithConflicts,
    handleEditSubmit,
    checkCustomerConflicts,
    checkSeriesConflicts,
    seriesSyncId,
    setSeriesSyncId,
  };
};

export default useBookingActions;
